import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../../assets/logoMoovicar.png';
import { useAxios } from '../../utils/hook/useAxios';

function ResetAccount() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const axios = useAxios();
  const { token } = useParams();

  // Force du mot de passe (0-4)
  const pwdStrength = (() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/\d/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();
  const strengthLabel = ['', 'Faible', 'Moyen', 'Bon', 'Fort'][pwdStrength];
  const strengthColor = ['', 'bg-danger', 'bg-warning', 'bg-info', 'bg-success'][pwdStrength];

  const isValid = password.length >= 8 && confirm === password && !!token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Lien invalide (token manquant)');
      return;
    }
    if (password.length < 8) {
      toast.error('8 caractères minimum');
      return;
    }
    if (password !== confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`/auth/reset-password`, { token: token, password: password });
      setDone(true);
    } catch {
      toast.error('Lien invalide ou expiré. Demandez un nouveau lien.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-vh-100 d-flex flex-column align-items-center justify-content-center bg-body-tertiary py-4">
      <ToastContainer position="top-center" />

      <div style={{ width: '100%', maxWidth: 420, padding: '0 1rem' }}>
        {/* Logo */}
        <div className="text-center mb-4">
          <Link to="/">
            <img src={logo} alt="Moovicar" height="48" />
          </Link>
        </div>

        {/* Card */}
        <div className="card border-0 shadow-sm rounded-4 p-4">
          {done ? (
            /* Succès */
            <div className="text-center py-2">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success-subtle mb-3"
                style={{ width: 64, height: 64 }}
              >
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: 28 }}></i>
              </div>
              <h5 className="fw-bold mb-2">Mot de passe mis à jour !</h5>
              <p className="text-muted mb-4" style={{ fontSize: 14 }}>
                Votre mot de passe a été réinitialisé avec succès.
              </p>
              <Link to="/signin" className="btn btn-primary w-100 rounded-3 py-2 fw-semibold">
                Se connecter
              </Link>
            </div>
          ) : (
            /* Formulaire */
            <>
              <div className="text-center mb-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle mb-3"
                  style={{ width: 56, height: 56 }}
                >
                  <i className="bi bi-shield-lock-fill text-primary" style={{ fontSize: 24 }}></i>
                </div>
                <h4 className="fw-bold mb-1">Nouveau mot de passe</h4>
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  Choisissez un mot de passe sécurisé.
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Nouveau mot de passe */}
                <div className="mb-1">
                  <label className="form-label small fw-semibold text-muted">
                    Nouveau mot de passe
                  </label>
                  <div className="position-relative">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      className="form-control rounded-3 pe-5"
                      placeholder="8 caractères minimum"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent me-1"
                      onClick={() => setShowPwd((p) => !p)}
                      tabIndex={-1}
                      style={{ zIndex: 5 }}
                    >
                      <i className={`bi ${showPwd ? 'bi-eye-slash' : 'bi-eye'} text-muted`}></i>
                    </button>
                  </div>
                </div>

                {/* Indicateur force */}
                {password.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex gap-1 mt-2">
                      {[1, 2, 3, 4].map((n) => (
                        <div
                          key={n}
                          className={`rounded flex-fill ${n <= pwdStrength ? strengthColor : 'bg-secondary-subtle'}`}
                          style={{ height: 4 }}
                        />
                      ))}
                    </div>
                    {strengthLabel && (
                      <p
                        className={`mb-0 mt-1 small ${['', 'text-danger', 'text-warning', 'text-info', 'text-success'][pwdStrength]}`}
                      >
                        Force : {strengthLabel}
                      </p>
                    )}
                  </div>
                )}

                {/* Confirmation */}
                <div className="mb-4">
                  <label className="form-label small fw-semibold text-muted">
                    Confirmer le mot de passe
                  </label>
                  <div className="position-relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      className={`form-control rounded-3 pe-5 ${
                        confirm && password
                          ? confirm === password
                            ? 'is-valid'
                            : 'is-invalid'
                          : ''
                      }`}
                      placeholder="Répéter le mot de passe"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent me-1"
                      onClick={() => setShowConfirm((p) => !p)}
                      tabIndex={-1}
                      style={{ zIndex: 5 }}
                    >
                      <i className={`bi ${showConfirm ? 'bi-eye-slash' : 'bi-eye'} text-muted`}></i>
                    </button>
                  </div>
                  {confirm && confirm !== password && (
                    <div className="invalid-feedback d-block" style={{ fontSize: 12 }}>
                      Les mots de passe ne correspondent pas.
                    </div>
                  )}
                  {confirm && confirm === password && (
                    <div className="valid-feedback d-block" style={{ fontSize: 12 }}>
                      <i className="bi bi-check-circle-fill me-1"></i>Les mots de passe
                      correspondent.
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100 rounded-3 py-2 fw-semibold"
                  disabled={loading || !isValid}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Réinitialisation…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shield-check me-2"></i>Réinitialiser
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Retour connexion */}
        <p className="text-center text-muted mt-3" style={{ fontSize: 14 }}>
          <Link to="/signin" className="text-muted text-decoration-none">
            <i className="bi bi-arrow-left me-1"></i>Retour à la connexion
          </Link>
        </p>
      </div>
    </section>
  );
}

export default ResetAccount;
